// Player cosmetics framework: titles, avatar frames, avatars, name badges, map badges.
// Catalog entries and Story `cosmeticRewardIds` can be filled later without
// changing the grant / equip / persist pipeline.

import characters from "../Characters.json";
import {
    getStoryLevelById,
    getStoryMainLevelCount,
    getTotalStoryLevels,
} from "../data/storyLevels";
import { getStoryLevelCosmeticRewardIds } from "../data/storyLevelRewards";
import { getCardImageSrc } from "./cardImages";
import { formatBaseCardDisplayName } from "./packReveal";
import { hasCompletedStoryLevel } from "./storyProgress";

export const COSMETICS_STATE_VERSION = 1;
export const LS_COSMETICS_KEY = "mmu_cosmetics_v1";
export const AVATAR_CARD_PREFIX = "avatar_card_";

export const COSMETIC_TYPES = {
    TITLE: "title",
    AVATAR_FRAME: "avatarFrame",
    AVATAR: "avatar",
    NAME_BADGE: "nameBadge",
    MAP_BADGE: "mapBadge",
};

export const COSMETIC_TYPE_LIST = Object.values(COSMETIC_TYPES);

/** Endless perfect-streak name badge tiers (threshold → color theme). */
export const STREAK_BADGE_TIERS = [
    { threshold: 100, tone: "green", badgeGlyph: "100" },
    { threshold: 150, tone: "sky", badgeGlyph: "150" },
    { threshold: 200, tone: "amber", badgeGlyph: "200" },
    { threshold: 250, tone: "purple", badgeGlyph: "250" },
    { threshold: 300, tone: "ember", badgeGlyph: "300" },
    { threshold: 350, tone: "rose", badgeGlyph: "350" },
    { threshold: 400, tone: "violet", badgeGlyph: "400" },
    { threshold: 450, tone: "steel", badgeGlyph: "450" },
    { threshold: 500, tone: "gold", badgeGlyph: "500" },
];

/**
 * Catalog of cosmetics. Add real items here when art/copy is ready.
 * Set `grantable: true` and attach the id to a Story level's
 * `cosmeticRewardIds` (or call unlockCosmetic) to ship it.
 */
export const COSMETICS_CATALOG = [
    {
        id: "title_overworld_savior",
        type: COSMETIC_TYPES.TITLE,
        name: "Savior of the Overworld",
        description: "Earned by completing the Overworld in Story Mode.",
        source: "story:12",
        grantable: true,
        placeholder: false,
    },
    {
        id: "title_underworld_ruler",
        type: COSMETIC_TYPES.TITLE,
        name: "Ruler of the Underworld",
        description: "Earned by completing the Underworld in Story Mode.",
        source: "story:19",
        grantable: true,
        placeholder: false,
    },
    {
        id: "title_open_beta",
        type: COSMETIC_TYPES.TITLE,
        name: "Open Beta Supporter",
        description: "Thank you for playing during Open Beta.",
        source: "open_beta",
        grantable: true,
        placeholder: false,
    },
    {
        id: "frame_journey",
        type: COSMETIC_TYPES.AVATAR_FRAME,
        name: "World Journey",
        description:
            "Beach, meadow, mountains, and starry space — earned by completing Story Mode.",
        source: "story:12",
        grantable: true,
        placeholder: false,
        visualKey: "journey",
        assetSrc: "/images/cosmetics/frame-story-journey.png",
    },
    {
        id: "frame_underworld_journey",
        type: COSMETIC_TYPES.AVATAR_FRAME,
        name: "Underworld Journey",
        description:
            "Lava, crystals, and cavern gloom — earned by completing the Underworld.",
        source: "story:19",
        grantable: true,
        placeholder: false,
        visualKey: "underworldJourney",
        assetSrc: "/images/cosmetics/frame-underworld-journey.png",
    },
    {
        id: "frame_gem_amethyst",
        type: COSMETIC_TYPES.AVATAR_FRAME,
        name: "Amethyst",
        description: "Unlocked by collecting the Amethyst gem corgi.",
        source: "collection:gem:Amethyst",
        grantable: true,
        placeholder: false,
        visualKey: "gemAmethyst",
        assetSrc: "/images/cosmetics/frame-amethyst.png",
        unlockCardId: 10011,
    },
    {
        id: "frame_gem_citrine",
        type: COSMETIC_TYPES.AVATAR_FRAME,
        name: "Citrine",
        description: "Unlocked by collecting the Citrine gem corgi.",
        source: "collection:gem:Citrine",
        grantable: true,
        placeholder: false,
        visualKey: "gemCitrine",
        assetSrc: "/images/cosmetics/frame-citrine.png",
        unlockCardId: 10012,
    },
    {
        id: "frame_gem_diamond",
        type: COSMETIC_TYPES.AVATAR_FRAME,
        name: "Diamond",
        description: "Unlocked by collecting the Diamond gem corgi.",
        source: "collection:gem:Diamond",
        grantable: true,
        placeholder: false,
        visualKey: "gemDiamond",
        assetSrc: "/images/cosmetics/frame-diamond.png",
        unlockCardId: 10013,
    },
    {
        id: "frame_gem_emerald",
        type: COSMETIC_TYPES.AVATAR_FRAME,
        name: "Emerald",
        description: "Unlocked by collecting the Emerald gem corgi.",
        source: "collection:gem:Emerald",
        grantable: true,
        placeholder: false,
        visualKey: "gemEmerald",
        assetSrc: "/images/cosmetics/frame-emerald.png",
        unlockCardId: 10005,
    },
    {
        id: "frame_gem_obsidian",
        type: COSMETIC_TYPES.AVATAR_FRAME,
        name: "Obsidian",
        description: "Unlocked by collecting the Obsidian gem corgi.",
        source: "collection:gem:Obsidian",
        grantable: true,
        placeholder: false,
        visualKey: "gemObsidian",
        assetSrc: "/images/cosmetics/frame-obsidian.png",
        unlockCardId: 10006,
    },
    {
        id: "frame_gem_opal",
        type: COSMETIC_TYPES.AVATAR_FRAME,
        name: "Opal",
        description: "Unlocked by collecting the Opal gem corgi.",
        source: "collection:gem:Opal",
        grantable: true,
        placeholder: false,
        visualKey: "gemOpal",
        assetSrc: "/images/cosmetics/frame-opal.png",
        unlockCardId: 10007,
    },
    {
        id: "frame_gem_ruby",
        type: COSMETIC_TYPES.AVATAR_FRAME,
        name: "Ruby",
        description: "Unlocked by collecting the Ruby gem corgi.",
        source: "collection:gem:Ruby",
        grantable: true,
        placeholder: false,
        visualKey: "gemRuby",
        assetSrc: "/images/cosmetics/frame-ruby.png",
        unlockCardId: 10008,
    },
    {
        id: "frame_gem_sapphire",
        type: COSMETIC_TYPES.AVATAR_FRAME,
        name: "Sapphire",
        description: "Unlocked by collecting the Sapphire gem corgi.",
        source: "collection:gem:Sapphire",
        grantable: true,
        placeholder: false,
        visualKey: "gemSapphire",
        assetSrc: "/images/cosmetics/frame-sapphire.png",
        unlockCardId: 10009,
    },
    {
        id: "frame_gem_topaz",
        type: COSMETIC_TYPES.AVATAR_FRAME,
        name: "Topaz",
        description: "Unlocked by collecting the Topaz gem corgi.",
        source: "collection:gem:Topaz",
        grantable: true,
        placeholder: false,
        visualKey: "gemTopaz",
        assetSrc: "/images/cosmetics/frame-topaz.png",
        unlockCardId: 10010,
    },
    {
        id: "frame_gem_rainbow_diamond",
        type: COSMETIC_TYPES.AVATAR_FRAME,
        name: "Rainbow Diamond",
        description:
            "Unlocked by collecting every Hidden Gem corgi in your Collection.",
        source: "collection:gems:all",
        grantable: true,
        placeholder: false,
        visualKey: "gemRainbowDiamond",
        assetSrc: "/images/cosmetics/frame-rainbow-diamond.png",
        unlockAllCardIds: [
            10005, 10006, 10007, 10008, 10009, 10010, 10011, 10012, 10013,
        ],
    },

    {
        id: "badge_open_beta",
        type: COSMETIC_TYPES.NAME_BADGE,
        name: "Open Beta",
        description: "Played during Open Beta.",
        source: "open_beta",
        grantable: true,
        placeholder: false,
        visualKey: "openBeta",
        badgeGlyph: "β",
        badgeTone: "steel",
        assetSrc: "/images/cosmetics/badge-open-beta.png",
    },
    {
        id: "badge_overworld",
        type: COSMETIC_TYPES.NAME_BADGE,
        name: "Overworld",
        description: "Complete the Overworld in Story Mode.",
        source: "story:12",
        grantable: true,
        placeholder: false,
        visualKey: "overworld",
        badgeGlyph: "☀",
        badgeTone: "sky",
        assetSrc: "/images/cosmetics/badge-overworld.png",
    },
    {
        id: "badge_underworld",
        type: COSMETIC_TYPES.NAME_BADGE,
        name: "Underworld",
        description: "Complete the Underworld in Story Mode.",
        source: "story:19",
        grantable: true,
        placeholder: false,
        visualKey: "underworld",
        badgeGlyph: "🔥",
        badgeTone: "purple",
        assetSrc: "/images/cosmetics/badge-underworld.png",
    },
    {
        id: "badge_hidden_gems",
        type: COSMETIC_TYPES.NAME_BADGE,
        name: "Hidden Gems",
        description: "Collect every Hidden Gem corgi.",
        source: "collection:gems:all",
        grantable: true,
        placeholder: false,
        visualKey: "hiddenGems",
        badgeGlyph: "◆",
        badgeTone: "prism",
        badgeRarity: "legendary",
        assetSrc: "/images/cosmetics/badge-hidden-gems.png",
    },
    {
        id: "badge_alpha_pack",
        type: COSMETIC_TYPES.NAME_BADGE,
        name: "Alpha Pack",
        description: "Collect every Alpha Edition corgi.",
        source: "collection:alpha:all",
        grantable: true,
        placeholder: false,
        visualKey: "alphaPack",
        badgeGlyph: "Α",
        badgeTone: "platinum",
        badgeRarity: "legendary",
        assetSrc: "/images/cosmetics/badge-alpha-pack.png",
    },
    {
        id: "badge_overachiever",
        type: COSMETIC_TYPES.NAME_BADGE,
        name: "Overachiever",
        description: "Unlock every corgi in the Collection.",
        source: "collection:all",
        grantable: true,
        placeholder: false,
        visualKey: "overachiever",
        badgeGlyph: "★",
        badgeTone: "amber",
        assetSrc: "/images/cosmetics/badge-overachiever.png",
    },
    ...STREAK_BADGE_TIERS.map(({ threshold, tone, badgeGlyph }) => ({
        id: `badge_perfect_streak_${threshold}`,
        type: COSMETIC_TYPES.NAME_BADGE,
        name: `Streak ${threshold}`,
        description: `Reach a perfect streak of ${threshold} in Endless Mode.`,
        source: `endless:perfect_streak:${threshold}`,
        grantable: true,
        placeholder: false,
        visualKey: `streak${threshold}`,
        badgeGlyph,
        badgeTone: tone,
        assetSrc: `/images/cosmetics/badge-streak-${threshold}.png`,
    })),
    {
        id: "uw_badge_l15",
        type: COSMETIC_TYPES.MAP_BADGE,
        name: "Underworld Badge (L15)",
        description: "Reserved map badge for clearing Ash and Mirrors.",
        source: "story:15",
        grantable: false,
        placeholder: true,
    },
];

export const OPEN_BETA_TITLE_ID = "title_open_beta";

/** Card ids for all Hidden Gem corgis (used by gem frame unlocks). */
export const GEM_CORGI_CARD_IDS = characters
    .filter((card) => String(card?.rarity || "").toLowerCase() === "gem")
    .map((card) => Number(card.id))
    .filter((id) => Number.isFinite(id));


export const RAINBOW_DIAMOND_FRAME_ID = "frame_gem_rainbow_diamond";

export const OPEN_BETA_NAME_BADGE_ID = "badge_open_beta";
export const NAME_BADGE_IDS = COSMETICS_CATALOG.filter(
    (item) => item.type === COSMETIC_TYPES.NAME_BADGE
).map((item) => item.id);

export const ALPHA_CORGI_CARD_IDS = characters
    .filter((card) => String(card?.rarity || "").toLowerCase() === "alpha")
    .map((card) => Number(card.id))
    .filter((id) => Number.isFinite(id));


/**
 * CSS class for a profile avatar frame cosmetic (or empty string).
 */
export function getAvatarFrameClassName(frameOrId = null) {
    const item =
        frameOrId && typeof frameOrId === "object"
            ? frameOrId
            : getCosmeticById(frameOrId);

    if (!item || item.type !== COSMETIC_TYPES.AVATAR_FRAME) {
        return "";
    }

    const key = String(item.visualKey || "").trim();
    if (!key) {
        return "";
    }

    return `profileImageFrame--${key}`;
}

const CATALOG_BY_ID = COSMETICS_CATALOG.reduce((map, item) => {
    map[item.id] = item;
    return map;
}, {});

const CHARACTERS_BY_ID = characters.reduce((map, card) => {
    const id = Number(card.id);
    if (Number.isFinite(id)) {
        map[id] = card;
        map[String(id)] = card;
    } else if (card.id != null) {
        map[String(card.id)] = card;
    }
    return map;
}, {});

function normalizeCardId(cardId) {
    if (cardId === null || cardId === undefined || cardId === "") {
        return null;
    }
    const n = Number(cardId);
    return Number.isFinite(n) ? n : String(cardId);
}

export function getAvatarCosmeticIdForCard(cardId) {
    const id = normalizeCardId(cardId);
    if (id === null) {
        return null;
    }
    return `${AVATAR_CARD_PREFIX}${id}`;
}

export function getCardIdFromAvatarCosmeticId(cosmeticId) {
    const raw = String(cosmeticId || "");
    if (!raw.startsWith(AVATAR_CARD_PREFIX)) {
        return null;
    }
    return normalizeCardId(raw.slice(AVATAR_CARD_PREFIX.length));
}

export function buildAvatarCosmeticFromCard(card) {
    if (!card) {
        return null;
    }
    const cardId = normalizeCardId(card.id);
    if (cardId === null) {
        return null;
    }
    const rarity = String(card.rarity || "").trim().toLowerCase();
    return {
        id: getAvatarCosmeticIdForCard(cardId),
        type: COSMETIC_TYPES.AVATAR,
        name: formatBaseCardDisplayName(card) || `Card ${cardId}`,
        description: "Unlocked from your Collection — equip as your Profile picture.",
        source: "collection",
        grantable: true,
        placeholder: false,
        cardId,
        rarity: rarity || "base",
        imageSrc: getCardImageSrc(card),
    };
}

export function isSpecialCollectionAvatar(itemOrCard = null) {
    const rarity = String(itemOrCard?.rarity || "")
        .trim()
        .toLowerCase();
    return rarity === "alpha" || rarity === "gem" || rarity === "founder";
}

export function createEmptyCosmeticsState() {
    return {
        version: COSMETICS_STATE_VERSION,
        unlockedIds: [],
        equipped: {
            titleId: null,
            avatarFrameId: null,
            avatarId: null,
            nameBadgeId: null,
            mapBadgeId: null,
        },
    };
}

function uniqueIds(ids = []) {
    return Array.from(
        new Set(
            (Array.isArray(ids) ? ids : [])
                .map((id) => String(id || "").trim())
                .filter(Boolean)
        )
    ).sort();
}

export function getCosmeticById(id) {
    if (!id) {
        return null;
    }
    const key = String(id);
    if (CATALOG_BY_ID[key]) {
        return CATALOG_BY_ID[key];
    }
    const cardId = getCardIdFromAvatarCosmeticId(key);
    if (cardId === null) {
        return null;
    }
    const card = CHARACTERS_BY_ID[cardId] || CHARACTERS_BY_ID[String(cardId)];
    return buildAvatarCosmeticFromCard(card);
}

export function listCosmeticsByType(type, { unlockedCardIds = null } = {}) {
    const catalogItems = COSMETICS_CATALOG.filter((item) => item.type === type);
    if (type !== COSMETIC_TYPES.AVATAR || !Array.isArray(unlockedCardIds)) {
        return catalogItems;
    }

    // Browser mode: Collection cards that the player has unlocked.
    const unlocked = new Set(
        unlockedCardIds.map((id) => normalizeCardId(id)).filter((id) => id !== null)
    );
    const avatars = characters
        .map((card) => buildAvatarCosmeticFromCard(card))
        .filter((item) => item && unlocked.has(item.cardId));

    return [...catalogItems, ...avatars];
}

export const COSMETIC_TYPE_LABELS = {
    [COSMETIC_TYPES.TITLE]: "Titles",
    [COSMETIC_TYPES.AVATAR_FRAME]: "Frames",
    [COSMETIC_TYPES.AVATAR]: "Avatars",
    [COSMETIC_TYPES.NAME_BADGE]: "Name Badges",
    [COSMETIC_TYPES.MAP_BADGE]: "Map Badges",
};

export function getCosmeticSourceLabel(item = null) {
    if (!item || typeof item !== "object") {
        return "Reward";
    }

    const source = String(item.source || "").trim();
    const storyMatch = source.match(/^story:(\d+)$/i);
    if (storyMatch) {
        return `Story Level ${storyMatch[1]}`;
    }

    if (/^open_beta$/i.test(source)) {
        return "Open Beta";
    }

    if (/^collection$/i.test(source)) {
        return "Collection";
    }

    const gemMatch = source.match(/^collection:gem:(.+)$/i);
    if (gemMatch) {
        return `Collect ${gemMatch[1]}`;
    }

    if (/^collection:gems:all$/i.test(source)) {
        return "Collect all Hidden Gems";
    }

    if (/^collection:alpha:all$/i.test(source)) {
        return "Collect all Alpha Edition";
    }

    if (/^collection:all$/i.test(source)) {
        return "Complete the Collection";
    }

    if (/^endless:perfect_streak:(\d+)$/i.test(source)) {
        const match = source.match(/^endless:perfect_streak:(\d+)$/i);
        return `Endless perfect streak ${match[1]}`;
    }

    if (source) {
        return source;
    }

    return "Reward";
}

/**
 * Sections for the Cosmetics browser screen.
 * Includes grantable items plus reserved placeholders (shown as coming soon).
 * Pass `unlockedCardIds` so Collection unlocks appear under Avatars.
 */
export function getCosmeticBrowserSections(
    state = null,
    { unlockedCardIds = [] } = {}
) {
    const normalized = normalizeCosmeticsState(state);
    const equipped = normalized.equipped;
    const cardIds = Array.isArray(unlockedCardIds) ? unlockedCardIds : [];

    const isEquipped = (item) => {
        if (item.type === COSMETIC_TYPES.TITLE) {
            return equipped.titleId === item.id;
        }
        if (item.type === COSMETIC_TYPES.AVATAR_FRAME) {
            return equipped.avatarFrameId === item.id;
        }
        if (item.type === COSMETIC_TYPES.AVATAR) {
            return equipped.avatarId === item.id;
        }
        if (item.type === COSMETIC_TYPES.NAME_BADGE) {
            return equipped.nameBadgeId === item.id;
        }
        if (item.type === COSMETIC_TYPES.MAP_BADGE) {
            return equipped.mapBadgeId === item.id;
        }
        return false;
    };

    return COSMETIC_TYPE_LIST.map((type) => {
        const items = listCosmeticsByType(type, {
            unlockedCardIds: cardIds,
        }).map((item) => {
            const unlocked =
                item.type === COSMETIC_TYPES.AVATAR && item.cardId != null
                    ? cardIds.some(
                          (id) => normalizeCardId(id) === item.cardId
                      ) || normalized.unlockedIds.includes(item.id)
                    : normalized.unlockedIds.includes(item.id);
            const comingSoon =
                Boolean(item.placeholder) || item.grantable === false;
            return {
                item,
                unlocked,
                equipped: unlocked && isEquipped(item),
                comingSoon: comingSoon && !unlocked,
            };
        });

        return {
            type,
            label: COSMETIC_TYPE_LABELS[type] || type,
            items,
            unlockedCount: items.filter((entry) => entry.unlocked).length,
        };
    });
}

export function normalizeCosmeticsState(raw = null) {
    const empty = createEmptyCosmeticsState();
    if (!raw || typeof raw !== "object") {
        return empty;
    }

    const unlockedIds = uniqueIds(raw.unlockedIds).filter((id) =>
        Boolean(getCosmeticById(id))
    );
    const equippedRaw =
        raw.equipped && typeof raw.equipped === "object" ? raw.equipped : {};

    const normalizeEquipped = (key, type) => {
        const value =
            equippedRaw[key] != null && equippedRaw[key] !== ""
                ? String(equippedRaw[key])
                : null;
        if (!value) {
            return null;
        }
        const item = getCosmeticById(value);
        if (!item || item.type !== type) {
            return null;
        }
        // Collection avatars are owned via card unlocks; allow equipped id
        // even before syncCollectionAvatars writes them into unlockedIds.
        if (type === COSMETIC_TYPES.AVATAR && item.cardId != null) {
            return value;
        }
        if (!unlockedIds.includes(value)) {
            return null;
        }
        return value;
    };

    return {
        version: COSMETICS_STATE_VERSION,
        unlockedIds,
        equipped: {
            titleId: normalizeEquipped("titleId", COSMETIC_TYPES.TITLE),
            avatarFrameId: normalizeEquipped(
                "avatarFrameId",
                COSMETIC_TYPES.AVATAR_FRAME
            ),
            avatarId: normalizeEquipped("avatarId", COSMETIC_TYPES.AVATAR),
            nameBadgeId: normalizeEquipped(
                "nameBadgeId",
                COSMETIC_TYPES.NAME_BADGE
            ),
            mapBadgeId: normalizeEquipped(
                "mapBadgeId",
                COSMETIC_TYPES.MAP_BADGE
            ),
        },
    };
}

export function mergeCosmeticsState(localState, remoteState) {
    const local = normalizeCosmeticsState(localState);
    const remote = normalizeCosmeticsState(remoteState);
    const unlockedIds = uniqueIds([
        ...local.unlockedIds,
        ...remote.unlockedIds,
    ]);

    // Prefer local equipped when still owned; else fall back to remote.
    const pickEquipped = (key) => {
        const localId = local.equipped[key];
        if (localId && unlockedIds.includes(localId)) {
            return localId;
        }
        const remoteId = remote.equipped[key];
        if (remoteId && unlockedIds.includes(remoteId)) {
            return remoteId;
        }
        return null;
    };

    return normalizeCosmeticsState({
        unlockedIds,
        equipped: {
            titleId: pickEquipped("titleId"),
            avatarFrameId: pickEquipped("avatarFrameId"),
            avatarId: pickEquipped("avatarId"),
            nameBadgeId: pickEquipped("nameBadgeId"),
            mapBadgeId: pickEquipped("mapBadgeId"),
        },
    });
}

export function isCosmeticUnlocked(cosmeticId, state = null) {
    const normalized = normalizeCosmeticsState(state);
    return normalized.unlockedIds.includes(String(cosmeticId || ""));
}

/**
 * Unlock a catalog cosmetic. Placeholders / non-grantable items are ignored
 * unless `force` is true (admin / migration).
 */
export function unlockCosmetic(state, cosmeticId, { force = false } = {}) {
    const normalized = normalizeCosmeticsState(state);
    const id = String(cosmeticId || "");
    const item = getCosmeticById(id);

    if (!item) {
        return { state: normalized, granted: false, item: null };
    }

    if (!force && item.grantable === false) {
        return { state: normalized, granted: false, item };
    }

    if (normalized.unlockedIds.includes(id)) {
        return { state: normalized, granted: false, item };
    }

    return {
        state: normalizeCosmeticsState({
            ...normalized,
            unlockedIds: [...normalized.unlockedIds, id],
        }),
        granted: true,
        item,
    };
}

export function unlockCosmetics(state, cosmeticIds = [], options = {}) {
    let next = normalizeCosmeticsState(state);
    const grantedItems = [];

    uniqueIds(cosmeticIds).forEach((id) => {
        const result = unlockCosmetic(next, id, options);
        next = result.state;
        if (result.granted && result.item) {
            grantedItems.push(result.item);
        }
    });

    return { state: next, grantedItems };
}

/** Grantable catalog cosmetics (excludes placeholders / reserved items). */
export function getGrantableCosmeticIds() {
    return COSMETICS_CATALOG.filter(
        (item) => item && item.grantable !== false && !item.placeholder
    ).map((item) => item.id);
}

/**
 * Unlock every currently grantable catalog cosmetic.
 * Collection avatars stay tied to owned cards (use syncCollectionAvatars).
 */
export function unlockAllGrantableCosmetics(state = null, options = {}) {
    return unlockCosmetics(state, getGrantableCosmeticIds(), options);
}

export function equipCosmetic(state, cosmeticId) {
    const normalized = normalizeCosmeticsState(state);
    const id = String(cosmeticId || "");
    const item = getCosmeticById(id);

    if (!item || !normalized.unlockedIds.includes(id)) {
        return { state: normalized, equipped: false, item: null };
    }

    const equipped = { ...normalized.equipped };

    if (item.type === COSMETIC_TYPES.TITLE) {
        equipped.titleId = id;
    } else if (item.type === COSMETIC_TYPES.AVATAR_FRAME) {
        equipped.avatarFrameId = id;
    } else if (item.type === COSMETIC_TYPES.AVATAR) {
        equipped.avatarId = id;
    } else if (item.type === COSMETIC_TYPES.NAME_BADGE) {
        equipped.nameBadgeId = id;
    } else if (item.type === COSMETIC_TYPES.MAP_BADGE) {
        equipped.mapBadgeId = id;
    } else {
        return { state: normalized, equipped: false, item };
    }

    return {
        state: normalizeCosmeticsState({ ...normalized, equipped }),
        equipped: true,
        item,
    };
}

export function unequipCosmeticType(state, type) {
    const normalized = normalizeCosmeticsState(state);
    const equipped = { ...normalized.equipped };

    if (type === COSMETIC_TYPES.TITLE) {
        equipped.titleId = null;
    } else if (type === COSMETIC_TYPES.AVATAR_FRAME) {
        equipped.avatarFrameId = null;
    } else if (type === COSMETIC_TYPES.AVATAR) {
        equipped.avatarId = null;
    } else if (type === COSMETIC_TYPES.NAME_BADGE) {
        equipped.nameBadgeId = null;
    } else if (type === COSMETIC_TYPES.MAP_BADGE) {
        equipped.mapBadgeId = null;
    }

    return normalizeCosmeticsState({ ...normalized, equipped });
}

export function getEquippedCosmetics(state = null) {
    const normalized = normalizeCosmeticsState(state);
    return {
        title: getCosmeticById(normalized.equipped.titleId),
        avatarFrame: getCosmeticById(normalized.equipped.avatarFrameId),
        avatar: getCosmeticById(normalized.equipped.avatarId),
        nameBadge: getCosmeticById(normalized.equipped.nameBadgeId),
        mapBadge: getCosmeticById(normalized.equipped.mapBadgeId),
    };
}

export function getStoryLevelCosmeticRewardIdsFromLevel(level = null) {
    if (!level || typeof level !== "object") {
        return [];
    }
    return getStoryLevelCosmeticRewardIds(level.id);
}

/**
 * Grant grantable cosmetics listed for a Story level in storyLevelRewards.js.
 * Placeholder / non-grantable reserved IDs are skipped until enabled.
 */
export function grantCosmeticsForStoryLevel(state, level) {
    const rewardIds = getStoryLevelCosmeticRewardIdsFromLevel(level);
    return unlockCosmetics(state, rewardIds);
}

/**
 * Backfill Story cosmetic rewards for levels already completed
 * (so new catalog items appear for existing saves).
 */
export function grantCosmeticsForCompletedStoryLevels(
    state,
    storyProgress = null
) {
    const completedIds =
        storyProgress && Array.isArray(storyProgress.completedLevelIds)
            ? storyProgress.completedLevelIds
            : [];

    let next = normalizeCosmeticsState(state);
    const grantedItems = [];

    completedIds.forEach((levelId) => {
        const storyLevel = getStoryLevelById(levelId);
        const result = grantCosmeticsForStoryLevel(next, storyLevel);
        next = result.state;
        grantedItems.push(...result.grantedItems);
    });

    return { state: next, grantedItems };
}

/**
 * If no avatar frame is equipped, equip the first owned grantable frame.
 */
export function autoEquipAvatarFrameIfEmpty(state, preferredId = null) {
    const normalized = normalizeCosmeticsState(state);
    if (normalized.equipped.avatarFrameId) {
        return { state: normalized, equipped: false, item: null };
    }

    const preferred =
        preferredId && normalized.unlockedIds.includes(String(preferredId))
            ? String(preferredId)
            : null;
    const fallback = normalized.unlockedIds.find((id) => {
        const item = getCosmeticById(id);
        return item && item.type === COSMETIC_TYPES.AVATAR_FRAME;
    });
    const pick = preferred || fallback;

    if (!pick) {
        return { state: normalized, equipped: false, item: null };
    }

    return equipCosmetic(normalized, pick);
}

/**
 * If no title is equipped, equip the preferred owned title (or first owned).
 */
export function autoEquipTitleIfEmpty(state, preferredId = null) {
    const normalized = normalizeCosmeticsState(state);
    if (normalized.equipped.titleId) {
        return { state: normalized, equipped: false, item: null };
    }

    const preferred =
        preferredId && normalized.unlockedIds.includes(String(preferredId))
            ? String(preferredId)
            : null;
    const fallback = normalized.unlockedIds.find((id) => {
        const item = getCosmeticById(id);
        return item && item.type === COSMETIC_TYPES.TITLE;
    });
    const pick = preferred || fallback;

    if (!pick) {
        return { state: normalized, equipped: false, item: null };
    }

    return equipCosmetic(normalized, pick);
}

/**
 * Mirror Collection unlocks into cosmetics avatar ids, and optionally equip
 * the current profile picture avatar when none is selected.
 */

/**
 * If no name badge is equipped, equip the preferred owned badge (or first owned).
 */
export function autoEquipNameBadgeIfEmpty(state, preferredId = null) {
    const normalized = normalizeCosmeticsState(state);
    if (normalized.equipped.nameBadgeId) {
        return { state: normalized, equipped: false, item: null };
    }

    const preferred =
        preferredId && normalized.unlockedIds.includes(String(preferredId))
            ? String(preferredId)
            : null;
    const fallback = normalized.unlockedIds.find((id) => {
        const item = getCosmeticById(id);
        return item && item.type === COSMETIC_TYPES.NAME_BADGE;
    });
    const pick = preferred || fallback;

    if (!pick) {
        return { state: normalized, equipped: false, item: null };
    }

    return equipCosmetic(normalized, pick);
}

export function syncCollectionAvatars(
    state = null,
    unlockedCardIds = [],
    { preferredCardId = null } = {}
) {
    const normalized = normalizeCosmeticsState(state);
    const cardIds = Array.isArray(unlockedCardIds) ? unlockedCardIds : [];
    const avatarIds = cardIds
        .map((cardId) => getAvatarCosmeticIdForCard(cardId))
        .filter((id) => Boolean(getCosmeticById(id)));

    let next = normalizeCosmeticsState({
        ...normalized,
        unlockedIds: uniqueIds([...normalized.unlockedIds, ...avatarIds]),
    });

    // Drop equipped avatar if that card is no longer owned.
    if (next.equipped.avatarId) {
        const equippedItem = getCosmeticById(next.equipped.avatarId);
        const stillOwned =
            equippedItem &&
            (equippedItem.cardId == null ||
                cardIds.some(
                    (id) => normalizeCardId(id) === equippedItem.cardId
                ));
        if (!stillOwned) {
            next = normalizeCosmeticsState({
                ...next,
                equipped: { ...next.equipped, avatarId: null },
            });
        }
    }

    if (!next.equipped.avatarId && avatarIds.length > 0) {
        const preferredAvatarId = getAvatarCosmeticIdForCard(preferredCardId);
        const pick =
            preferredAvatarId && avatarIds.includes(preferredAvatarId)
                ? preferredAvatarId
                : avatarIds[0];
        const equipped = equipCosmetic(next, pick);
        if (equipped.equipped) {
            next = equipped.state;
        }
    }

    return next;
}

/**
 * Unlock gem avatar frames based on owned Hidden Gem corgis.
 * Each gem grants its matching frame; owning every gem also grants
 * the Rainbow Diamond frame.
 */
export function syncGemFramesFromCollection(state = null, unlockedCardIds = []) {
    const owned = new Set(
        (Array.isArray(unlockedCardIds) ? unlockedCardIds : [])
            .map((id) => normalizeCardId(id))
            .filter((id) => id !== null)
    );

    const frameIds = COSMETICS_CATALOG.filter((item) => {
        if (item.type !== COSMETIC_TYPES.AVATAR_FRAME) {
            return false;
        }
        if (item.unlockCardId != null) {
            return owned.has(normalizeCardId(item.unlockCardId));
        }
        if (Array.isArray(item.unlockAllCardIds) && item.unlockAllCardIds.length) {
            return item.unlockAllCardIds.every((id) =>
                owned.has(normalizeCardId(id))
            );
        }
        return false;
    }).map((item) => item.id);

    return unlockCosmetics(state, frameIds).state;
}

/**
 * Sync Collection-owned avatars and gem frames in one pass.
 */

/**
 * Unlock name badges from story, collection, endless, and Open Beta progress.
 */
export function syncNameBadgesFromProgress(
    state = null,
    {
        unlockedCardIds = [],
        storyProgress = null,
        endlessStats = null,
    } = {}
) {
    const owned = new Set(
        (Array.isArray(unlockedCardIds) ? unlockedCardIds : [])
            .map((id) => normalizeCardId(id))
            .filter((id) => id !== null)
    );
    const idsToUnlock = [];

    // Open Beta badge is available to every player during Open Beta.
    idsToUnlock.push(OPEN_BETA_NAME_BADGE_ID);

    if (hasCompletedStoryLevel(getStoryMainLevelCount(), storyProgress)) {
        idsToUnlock.push("badge_overworld");
    }
    if (hasCompletedStoryLevel(getTotalStoryLevels(), storyProgress)) {
        idsToUnlock.push("badge_underworld");
    }

    if (
        GEM_CORGI_CARD_IDS.length > 0 &&
        GEM_CORGI_CARD_IDS.every((id) => owned.has(normalizeCardId(id)))
    ) {
        idsToUnlock.push("badge_hidden_gems");
    }

    if (
        ALPHA_CORGI_CARD_IDS.length > 0 &&
        ALPHA_CORGI_CARD_IDS.every((id) => owned.has(normalizeCardId(id)))
    ) {
        idsToUnlock.push("badge_alpha_pack");
    }

    const catalogCardIds = characters
        .map((card) => normalizeCardId(card.id))
        .filter((id) => id !== null);
    if (
        catalogCardIds.length > 0 &&
        catalogCardIds.every((id) => owned.has(id))
    ) {
        idsToUnlock.push("badge_overachiever");
    }

    const bestStreak = Math.max(
        Number(endlessStats?.bestPerfectStreak) || 0,
        Number(endlessStats?.bestPerfectStreak) || 0
    );
    STREAK_BADGE_TIERS.forEach(({ threshold }) => {
        if (bestStreak >= threshold) {
            idsToUnlock.push(`badge_perfect_streak_${threshold}`);
        }
    });

    return unlockCosmetics(state, idsToUnlock).state;
}

export function syncCollectionCosmetics(
    state = null,
    unlockedCardIds = [],
    options = {}
) {
    const {
        storyProgress = null,
        endlessStats = null,
        preferredCardId = null,
        ...avatarOptions
    } = options || {};
    const withAvatars = syncCollectionAvatars(state, unlockedCardIds, {
        preferredCardId,
        ...avatarOptions,
    });
    const withFrames = syncGemFramesFromCollection(
        withAvatars,
        unlockedCardIds
    );
    return syncNameBadgesFromProgress(withFrames, {
        unlockedCardIds,
        storyProgress,
        endlessStats,
    });
}

/**
 * Grant the Open Beta Supporter title to every player during Open Beta.
 */
export function grantOpenBetaTitle(state = null) {
    return unlockCosmetic(state, OPEN_BETA_TITLE_ID);
}

export function formatCosmeticUnlockMessages(grantedItems = []) {
    return (Array.isArray(grantedItems) ? grantedItems : [])
        .filter((item) => item && item.name)
        .map((item) => {
            if (item.type === COSMETIC_TYPES.TITLE) {
                return `Unlocked title: ${item.name}`;
            }
            if (item.type === COSMETIC_TYPES.AVATAR_FRAME) {
                return `Unlocked frame: ${item.name}`;
            }
            if (item.type === COSMETIC_TYPES.AVATAR) {
                return `Unlocked avatar: ${item.name}`;
            }
            if (item.type === COSMETIC_TYPES.NAME_BADGE) {
                return `Unlocked name badge: ${item.name}`;
            }
            if (item.type === COSMETIC_TYPES.MAP_BADGE) {
                return `Unlocked badge: ${item.name}`;
            }
            return `Unlocked: ${item.name}`;
        });
}

export function loadCosmeticsState() {
    try {
        const raw = window.localStorage.getItem(LS_COSMETICS_KEY);
        if (!raw) {
            return createEmptyCosmeticsState();
        }
        return normalizeCosmeticsState(JSON.parse(raw));
    } catch {
        return createEmptyCosmeticsState();
    }
}

export function saveCosmeticsState(state) {
    const normalized = normalizeCosmeticsState(state);
    try {
        window.localStorage.setItem(
            LS_COSMETICS_KEY,
            JSON.stringify(normalized)
        );
    } catch {
        // ignore localStorage failures
    }
    return normalized;
}
